import React, { useEffect, useMemo, useState } from "react";
import useStyles from "./styles";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  FormGroup,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import CeroInput from "../../../../components/CeroInput";
import CeroAutoComplete from "../../../../components/CeroAutoComplete";
import CeroPhoneInput from "../../../../components/CeroPhoneInput";
import CeroDate from "../../../../components/CeroDate";
import CeroChipSelect from "../../../../components/CeroChipSelect";

import RiskOpportunityTable from "./IfrsTables/ifrsTable1";
import OpportunityEffectsTable from "./IfrsTables/ifrsTable2";
import RiskActionsProgressTable from "./IfrsTables/ifrsTable3";
import FinancialEffectsRiskTable from "./IfrsTables/ifrstable4";
import TimeHorizonABCTable from "./IfrsTables/ifrstable5";
import SustainabilityRiskAssessmentTable from "./IfrsTables/ifrstable6";
import TargetsDisclosureTable from "./IfrsTables/ifrsTable7";
import ClimateRiskMatrixTable from "./IfrsTables/ifrsTable8";
import AnticipatedFinancialEffectsTable from "./IfrsTables/ifrsTable9";
import ElementsConsideredTable from "./IfrsTables/ifrsTable10";
import EmissionsMeasurementTable from "./IfrsTables/ifrsTable11";
import EmissionsByTypeRiskOppTable from "./IfrsTables/ifrsTable12";
import TargetDetailsTable from "./IfrsTables/ifrsTable13";

function IfrsQuestionnaire({
  handleCheckboxChange,
  handleSingleSelect,
  handleTextInputChange,
  handleDateInputChange,
  textAnswers,
  selectedValues,
  dateAnswers,
  setShowSaveSubmit,

  riskOpportunityData,
  setRiskOpportunityData,
  opportunityData,
  setOpportunityData,
  riskActionsProgressData,
  setRiskActionsProgressData,
  financialEffectsData,
  setFinancialEffectsData,
  timeHorizonABCTableData,
  setTimeHorizonABCTableData,
  sustainabilityRiskAssessData,
  setSustainabilityRiskAssessData,
  targetsDisclosureData,
  setTargetsDisclosureData,
  climateRiskMatrixData,
  setClimateRiskMatrixData,
  anticipatedFinancialEffectsData,
  setAnticipatedFinancialEffectsData,
  elementsConsideredEC,
  setElementsConsideredEC,
  emissionsMeasurementTE,
  setEmissionsMeasurementTE,
  emissionsByTypeERO,
  setEmissionsByTypeERO,
  targetDetailsTDT,
  setTargetDetailsTDT,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const questions = useSelector((state) => {
    const data = state.emission.getStandardsQuestionnaire?.data?.questions_list;
    return Array.isArray(data) ? data : [];
  });
  // put this near your other effects
  const [page, setPage] = useState(1);

  // hide buttons on entry and whenever page changes
  useEffect(() => {
    setShowSaveSubmit?.(page === 3);
    return () => setShowSaveSubmit?.(false);
  }, [page, setShowSaveSubmit]);

  // reset to page 1 when this instance mounts
  useEffect(() => {
    setPage(1);
    setShowSaveSubmit?.(false);
  }, []);

  // optional: if a different questionnaire loads, reset again
  useEffect(() => {
    if (questions?.length) {
      setPage(1);
      setShowSaveSubmit?.(false);
    }
  }, [questions?.length, questions?.[0]?.id]);

  const norm = (s = "") => s.replace(/\s+/g, " ").replace(/[-–—]/g, "-").trim();
  const S2_MARK = "IFRS S2 - Climate-related Disclosures";

  const splitIndex = questions.findIndex(
    (q) => norm(q.main_section) === norm(S2_MARK)
  );
  const GR_MARK = "GENERAL REQUIREMENTS";

  const splitIndexS2 = questions.findIndex(
    (q) => norm(q.main_section) === norm(S2_MARK)
  );

  const splitIndexGR = questions.findIndex(
    (q) => norm(q.main_section) === norm(GR_MARK)
  );

  const firstPageQuestions =
    splitIndexS2 >= 0 ? questions.slice(0, splitIndexS2) : questions;

  const secondPageQuestions =
    splitIndexS2 >= 0 && splitIndexGR > splitIndexS2
      ? questions.slice(splitIndexS2, splitIndexGR)
      : [];

  const thirdPageQuestions =
    splitIndexGR >= 0 ? questions.slice(splitIndexGR) : [];

  let currentQuestions = [];
  if (page === 1) currentQuestions = firstPageQuestions;
  else if (page === 2) currentQuestions = secondPageQuestions;
  else currentQuestions = thirdPageQuestions;

  // Helpers
  const toRoman = (n) =>
    ({
      1: "I",
      2: "II",
      3: "III",
      4: "IV",
      5: "V",
      6: "VI",
      7: "VII",
      8: "VIII",
      9: "IX",
      10: "X",
      11: "XI",
      12: "XII",
      13: "XIII",
      14: "XIV",
      15: "XV",
    }[n] || n);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");

  const formatSustainabilityRisksQuestion = (text = "") => {
    const [rawHeading, rest] = text.split(/Consider that/i);
    const heading = (rawHeading || "").trim().replace(/\.$/, "");

    if (!rest) return { heading, bullets: [] };

    const cleaned = rest.replace(/^.*time horizons can:\s*/i, "");

    const parts = cleaned.split(/\s*(\([a-ziv]+\))\s*/i).filter(Boolean);

    const bullets = [];
    for (let i = 0; i < parts.length; i++) {
      const isMarker = /^\([a-ziv]+\)$/i.test(parts[i]);
      if (isMarker) {
        const label = parts[i];
        const text = (parts[i + 1] || "").trim();
        bullets.push(`${label} ${text}`);
        i++;
      } else if (parts.length === 1) {
        bullets.push(parts[i].trim());
      }
    }
    return { heading, bullets };
  };

  // Extract "A - ... B - ... C - ..." from a question. If not present,
  // fall back to table_header[2..4].
  const splitABCLegend = (text = "", headers = []) => {
    let title = text || "";
    let legend = [];

    const m = text && text.match(/\bA\s*[-–—]\s*/i);
    if (m) {
      title = text
        .slice(0, m.index)
        .trim()
        .replace(/\s*\.$/, "");
      const tail = text.slice(m.index).trim();
      const parts = tail.split(/\s(?=[ABC]\s*[-–—]\s*)/gi); // ["A - …", "B - …", "C - …"]

      legend = parts
        .map((p) => {
          const mm = p.match(/^([ABC])\s*[-–—]\s*(.+)$/i);
          if (!mm) return null;
          const k = mm[1].toUpperCase();
          const v = mm[2].trim().replace(/\s*\.$/, "");
          return `${k} – ${v}`;
        })
        .filter(Boolean);
    } else if (headers?.[2] && headers?.[3] && headers?.[4]) {
      legend = [`A – ${headers[2]}`, `B – ${headers[3]}`, `C – ${headers[4]}`];
    }
    return { title, legend };
  };

  const hasPager = page < 3;

  const buildDicts = (subset) => {
    const qDict = {};
    const subDict = {};
    subset.forEach((q) => {
      const { main_section, section, sub_section, question_id } = q;
      if (!qDict[main_section]) qDict[main_section] = {};
      if (!qDict[main_section][section]) qDict[main_section][section] = {};
      if (!qDict[main_section][section][sub_section])
        qDict[main_section][section][sub_section] = [];

      if (question_id && question_id !== "None") {
        if (!subDict[question_id]) subDict[question_id] = [];
        subDict[question_id].push(q);
      }
      qDict[main_section][section][sub_section].push(q);
    });
    return { questionDict: qDict, subQuestionDict: subDict };
  };

  const startsWithOther = (s = "") => /^\s*others?\b/i.test(String(s).trim());

  const { questionDict, subQuestionDict } = buildDicts(currentQuestions);

  const pageIdMap = new Map(
    currentQuestions.map((it, idx) => [it.id, { q: it, idx }])
  );
  const IFRS4_ID = "68ad3089ea1e907f8e898a47";
  const CAPACITY_ID = "68ad3089ea1e907f8e898aa5";
  // --- Generic OPTION→CHILD rules for `condition: "sub_option"` ---
  const WHERE_CONCENTRATED_ID = "68ad3089ea1e907f8e898a6f";
  const SUBOPTION_RULES = {
    "68ad3089ea1e907f8e898aee": {
      "Legal or regulatory prohibition": [
        "68ad3089ea1e907f8e898aef",
        "68ad3089ea1e907f8e898af0",
      ],
      "Commercially sensitive information": [
        "68ad3089ea1e907f8e898af1",
        "68ad3089ea1e907f8e898af2",
        "68ad3089ea1e907f8e898af3",
      ],
    },

    "68ad3089ea1e907f8e898aca": {
      "Alongside other disclosures for other requirements": [
        "68ad3089ea1e907f8e898acb",
      ],
    },
  };
  // use this one style for every “Other / specify” TextField
  const TF_NO_BLUE = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,

      /* default border */
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "black",
      },

      /* hover */
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "black",
      },

      /* focused (this is the blue you see) */
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "black !important",
        borderWidth: "1px", // optional: keep it from getting thicker on focus
      },

      /* remove any shadow/outline some browsers add */
      boxShadow: "none",
      outline: "none",
    },

    /* also kill the input’s native focus ring */
    "& .MuiOutlinedInput-input:focus": {
      outline: "none",
    },
    "& .MuiOutlinedInput-root:focus-within": {
      boxShadow: "none",
      outline: "none",
    },
  };

  const getOptionChildren = (controllerId, optionLabel) => {
    const ids = SUBOPTION_RULES?.[controllerId]?.[optionLabel] || [];
    return ids.map((cid) => getQById(cid)).filter(Boolean);
  };

  // child shown under "Alongside other disclosures..." that must be inline
  const INLINE_YN_ID = "68ad3089ea1e907f8e898acb";

  const renderInlineYesNo = (child) => (
    <Box
      key={child.id}
      sx={{ gridColumn: "1 / -1", width: "100%", mt: 1, ml: 12 }}
    >
      {/* bullet before the question */}
      <Typography
        fontSize={16}
        fontWeight={300}
        sx={{
          mb: 1,
          pl: 2.5,
          position: "relative",
          "&::before": {
            content: '"•"',
            position: "absolute",
            left: 0,
            top: -1,
            fontSize: 22,
            lineHeight: 1,
            color: "#5A404E",
          },
        }}
      >
        {child.question}
      </Typography>

      {/* keep Yes/No horizontal */}
      <FormGroup row sx={{ columnGap: 3, alignItems: "center", ml: 3 }}>
        {child.options?.map((option, idx) => (
          <FormControlLabel
            key={idx}
            control={
              <Checkbox
                className={classes.selectCheckbox}
                checked={selectedValues?.[child.id] === option}
                onChange={() => handleSingleSelect(child.id, option)}
                value={option}
                sx={{
                  color: "#5A404E !important",
                  "&.Mui-checked": { color: "#8C2148 !important" },
                }}
              />
            }
            label={option}
          />
        ))}
      </FormGroup>
    </Box>
  );
  const optionTextKey = (qid, option) => `${qid}::${option}`;
  const isOptionPicked = (parentId, optionLabel) =>
    (selectedValues?.[parentId] || []).some(
      (v) => _normOpt(v) === _normOpt(optionLabel)
    );
  const YES_WHERE_ID = "68ad3089ea1e907f8e898aca";
  const INLINE_SUBOPTION_PARENTS = new Set([YES_WHERE_ID]);
  const SOC_PARENT_ID = "68ad3089ea1e907f8e898aee";
  const VERTICAL_ONLY_IDS = new Set([
    "68ad3089ea1e907f8e898aca",
    SOC_PARENT_ID,
    WHERE_CONCENTRATED_ID,
    CAPACITY_ID,
  ]);
  // fixed width only for the SOC text fields
  const SOC_FIXED_TEXT_IDS = new Set([
    "68ad3089ea1e907f8e898aef", // a. What type of information was not disclosed?
    "68ad3089ea1e907f8e898af0", // b. What is the source of the restriction?
  ]);
  const SOC_FIXED_TEXT_WIDTH = 620; // match the “perfect” width from your 2nd screenshot

  const renderChildOptions = (child) => {
    const isSingle =
      child.question_type === "choose_from_option" ||
      child.question_type === "Choose from Options";
    const isMulti = child.question_type === "multiselect_check_box";
    if (!isSingle && !isMulti) return null;

    const cols = VERTICAL_ONLY_IDS.has(child.id)
      ? "1fr"
      : `repeat(${
          child.options?.some((o) => o.split(" ").length > (isMulti ? 6 : 5))
            ? 2
            : 4
        }, 1fr)`;

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols,
          gap: "12px",
          textAlign: "left",
        }}
      >
        {child.options?.map((option, idx) => {
          const checked = isSingle
            ? selectedValues?.[child.id] === option
            : selectedValues?.[child.id]?.includes(option) || false;
          const onChange = isSingle
            ? () => handleSingleSelect(child.id, option)
            : (e) => handleCheckboxChange(e, child.id);

          return (
            <FormControlLabel
              key={idx}
              control={
                <Checkbox
                  className={classes.selectCheckbox}
                  checked={checked}
                  onChange={onChange}
                  value={option}
                  sx={{
                    color: "#5A404E !important",
                    "&.Mui-checked": { color: "#8C2148 !important" },
                    ...(isMulti ? { marginRight: "8px" } : {}),
                  }}
                />
              }
              label={option}
              sx={{ margin: "0 !important" }}
            />
          );
        })}
      </div>
    );
  };

  const _normOpt = (s = "") => s.toLowerCase().replace(/\s+/g, " ").trim();

  const shouldShowByOption = (controllerId, childId, selectedValues) => {
    const rules = SUBOPTION_RULES[controllerId];
    if (!rules) return false;
    const sel = selectedValues?.[controllerId];
    if (!Array.isArray(sel)) return false;

    return Object.entries(rules).some(([optLabel, childIds]) => {
      const optPicked = sel.some((v) => _normOpt(v) === _normOpt(optLabel));
      return optPicked && childIds.includes(childId);
    });
  };

  const SOC_GROUPS = {
    "Legal or regulatory prohibition": [
      "68ad3089ea1e907f8e898aef",
      "68ad3089ea1e907f8e898af0",
    ],
    "Commercially sensitive information": [
      "68ad3089ea1e907f8e898af1",
      "68ad3089ea1e907f8e898af2",
      "68ad3089ea1e907f8e898af3",
    ],
  };
  const SOC_CHILD_IDS = new Set(Object.values(SOC_GROUPS).flat());

  const getQById = (id) =>
    pageIdMap.get(id)?.q || currentQuestions.find((x) => x.id === id) || null;

  const renderSOCChild = (child) => {
    const gridClass =
      child.question_type === "text" || child.question_type === "Text Field"
        ? classes.inputGrid
        : classes.gridItem;

    return (
      <Grid key={child.id} className={gridClass}>
        {!!child.question && (
          <Typography
            fontSize={18}
            fontWeight={500}
            variant="h3"
            paddingLeft={4}
            paddingBottom={2}
          >
            {child.question}
          </Typography>
        )}

        <Box display="flex" paddingLeft={6} flexWrap="wrap">
          {(child.question_type === "choose_from_option" ||
            child.question_type === "Choose from Options") && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "12px",
                textAlign: "left",
              }}
            >
              {child.options?.map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      className={classes.selectCheckbox}
                      checked={selectedValues?.[child.id] === option}
                      onChange={() => handleSingleSelect(child.id, option)}
                      value={option}
                      sx={{
                        color: "#5A404E !important",
                        "&.Mui-checked": { color: "#8C2148 !important" },
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </div>
          )}

          {child.question_type === "multiselect_check_box" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "12px",
                textAlign: "left",
              }}
            >
              {child.options?.map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      className={classes.selectCheckbox}
                      checked={
                        selectedValues?.[child.id]?.includes(option) || false
                      }
                      onChange={(e) => handleCheckboxChange(e, child.id)}
                      value={option}
                      sx={{
                        color: "#5A404E !important",
                        "&.Mui-checked": { color: "#8C2148 !important" },
                        marginRight: "8px",
                      }}
                    />
                  }
                  label={option}
                  sx={{ margin: "0 !important" }}
                />
              ))}
            </div>
          )}

          {(child.question_type === "text" ||
            child.question_type === "Text Field") && (
            <Box
              minHeight={80}
              sx={{
                width: SOC_FIXED_TEXT_IDS.has(child.id)
                  ? SOC_FIXED_TEXT_WIDTH
                  : "100%",
              }}
            >
              <CeroInput
                className={classes.textAreaContainer}
                rows={3}
                placeholder="Please type your Answer"
                id={`answer-${child.id}`}
                name={`answer-${child.id}`}
                label="Please type your Answer"
                multiline
                value={textAnswers[child.id] || ""}
                onChange={(e) =>
                  handleTextInputChange(child.id, e.target.value)
                }
              />
            </Box>
          )}

          {child.question_type === "date" && (
            <CeroDate
              label="Date"
              value={dateAnswers[child.id] || ""}
              onChange={(date) => handleDateInputChange(child.id, date)}
              year={2025}
            />
          )}
        </Box>
      </Grid>
    );
  };

  const SectionIntro = ({ text }) => (
    <Typography
      variant="body1"
      fontSize={16}
      fontWeight={500}
      paddingTop="8px"
      paddingBottom="16px"
      paddingLeft="4px"
      sx={{ color: "black", width: "100%" }}
    >
      {text}
    </Typography>
  );

  const renderIfrsTable = (question) => {
    switch (question.condition) {
      case "ifrs_table_1":
        return (
          <RiskOpportunityTable
            riskOpportunityData={riskOpportunityData}
            setRiskOpportunityData={setRiskOpportunityData}
          />
        );
      case "ifrs_table_2":
        return (
          <OpportunityEffectsTable
            opportunityData={opportunityData}
            setOpportunityData={setOpportunityData}
          />
        );
      case "ifrs_table_3":
        return (
          <RiskActionsProgressTable
            riskActionsProgressData={riskActionsProgressData}
            setRiskActionsProgressData={setRiskActionsProgressData}
          />
        );
      case "ifrs_table_4":
        return (
          <FinancialEffectsRiskTable
            financialEffectsData={financialEffectsData}
            setFinancialEffectsData={setFinancialEffectsData}
          />
        );
      case "ifrs_table_5":
        return (
          <TimeHorizonABCTable
            timeHorizonABCTableData={timeHorizonABCTableData}
            setTimeHorizonABCTableData={setTimeHorizonABCTableData}
          />
        );
      case "ifrs_table_6":
        return (
          <SustainabilityRiskAssessmentTable
            sustainabilityRiskAssessData={sustainabilityRiskAssessData}
            setSustainabilityRiskAssessData={setSustainabilityRiskAssessData}
          />
        );
      case "ifrs_table_7":
        return (
          <TargetsDisclosureTable
            targetsDisclosureData={targetsDisclosureData}
            setTargetsDisclosureData={setTargetsDisclosureData}
          />
        );
      case "ifrs_table_8":
        return (
          <ClimateRiskMatrixTable
            climateRiskMatrixData={climateRiskMatrixData}
            setClimateRiskMatrixData={setClimateRiskMatrixData}
          />
        );
      case "ifrs_table_9":
        return (
          <AnticipatedFinancialEffectsTable
            anticipatedFinancialEffectsData={anticipatedFinancialEffectsData}
            setAnticipatedFinancialEffectsData={
              setAnticipatedFinancialEffectsData
            }
          />
        );
      case "ifrs_table_10":
        return (
          <ElementsConsideredTable
            elementsConsideredEC={elementsConsideredEC}
            setElementsConsideredEC={setElementsConsideredEC}
          />
        );
      case "ifrs_table_11":
        return (
          <EmissionsMeasurementTable
            emissionsMeasurementTE={emissionsMeasurementTE}
            setEmissionsMeasurementTE={setEmissionsMeasurementTE}
          />
        );
      case "ifrs_table_12":
        return (
          <EmissionsByTypeRiskOppTable
            emissionsByTypeERO={emissionsByTypeERO}
            setEmissionsByTypeERO={setEmissionsByTypeERO}
          />
        );
      case "ifrs_table_13":
        return (
          <TargetDetailsTable
            targetDetailsTDT={targetDetailsTDT}
            setTargetDetailsTDT={setTargetDetailsTDT}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <div>
      {questions.length > 0 && (
        <Grid mb={0}>
          <div
            className={`${classes.questionList} ${
              hasPager ? classes.pagePadForPager : ""
            }`}
          >
            {Object.keys(questionDict).map((main, mainIdx) => (
              <React.Fragment key={mainIdx}>
                {/* MAIN SECTION HEADING (e.g., GENERAL REQUIREMENTS) */}
                <Typography
                  className={classes.mainSectionHeading}
                  component="h2"
                >
                  <Typography fontWeight={700}>
                    {main || "Untitled Section"}
                  </Typography>
                </Typography>

                {Object.keys(questionDict[main]).map((section, secIdx) => {
                  let displayCounter = 1;
                  const deferredByController = new Map();

                  return (
                    <div key={`${mainIdx}-${secIdx}`}>
                      {typeof section === "string" &&
                        section.toLowerCase().includes("governance")}

                      <Typography
                        fontSize={19}
                        fontWeight={600}
                        paddingTop="10px"
                      >
                        {`${toRoman(secIdx + 1)}. ${section}`}
                      </Typography>

                      {Object.entries(questionDict[main][section]).map(
                        ([subSectionName, items], subIdx) => {
                          const displayedPrinciples = new Set();

                          const idMap = new Map(
                            items.map((it, idx) => [it.id, { q: it, idx }])
                          );
                          const _norm = (s = "") =>
                            s.toLowerCase().replace(/\s+/g, " ").trim();
                          const _stripLead = (s = "") =>
                            s
                              .replace(/^[\s.\-–—•\(\[\{\'\"\u2022]+/g, "")
                              .trim();
                          const getIfType = (text = "") => {
                            const t = _norm(_stripLead(text));
                            if (
                              /^if\s+yes\b/.test(t) ||
                              /^->\s*if\s+yes\b/.test(t) ||
                              /^if\s+available\b/.test(t) ||
                              /^if\s+so\b/.test(t)
                            )
                              return "yes";
                            if (/^if\s+no\b/.test(t) || /^if\s+not\b/.test(t))
                              return "no";
                            return null;
                          };
                          const looksYesNoQ = (cand = {}) => {
                            const type = String(
                              cand.question_type || ""
                            ).toLowerCase();
                            // only a single-select can be a controller
                            if (
                              ![
                                "choose_from_option",
                                "choose from options",
                              ].includes(type)
                            )
                              return false;

                            const opts = (cand.options || []).map((o) =>
                              _norm(String(o || ""))
                            );
                            // must have BOTH yes and no
                            return opts.includes("yes") && opts.includes("no");
                          };

                          const findParentId = (q, items, qIdx) => {
                            if (q.question_id && q.question_id !== "None")
                              return q.question_id;
                            for (let i = qIdx - 1; i >= 0; i--) {
                              const cand = items[i] || {};
                              if (looksYesNoQ(cand)) return cand.id;
                            }
                            return null;
                          };

                          // non-controller = not a Yes/No single-select and not a multiselect
                          const isNonControllerParent = (qq = {}) =>
                            !looksYesNoQ(qq) &&
                            qq?.question_type !== "multiselect_check_box";

                          const showByParentChoice = (
                            need,
                            parentId,
                            selectedValues
                          ) => {
                            const sel = selectedValues?.[parentId];
                            if (Array.isArray(sel)) {
                              const hasYes = sel.some(
                                (v) => _norm(v) === "yes"
                              );
                              const hasNo = sel.some((v) => _norm(v) === "no");
                              return need === "yes"
                                ? hasYes
                                : need === "no"
                                ? hasNo
                                : false;
                            }
                            const v = _norm(sel || "");
                            return need === "yes"
                              ? v === "yes"
                              : need === "no"
                              ? v === "no"
                              : false;
                          };

                          const resolveSubChain = (q, qIdx) => {
                            if (
                              q?.condition === "sub_option" &&
                              q?.question_id &&
                              q?.question_id !== "None"
                            ) {
                              return {
                                isSubQuestion: true,
                                ifType: null,
                                controllerId: q.question_id,
                              };
                            }

                            let ifType = getIfType(q.question || "");
                            let controllerId = null;
                            let cur = q;
                            let guard = 0;

                            while (guard++ < 20) {
                              let parent = null;

                              if (
                                cur.question_id &&
                                cur.question_id !== "None"
                              ) {
                                parent =
                                  pageIdMap.get(cur.question_id)?.q ||
                                  idMap.get(cur.question_id)?.q ||
                                  null;
                              } else {
                                const pid = findParentId(cur, items, qIdx);
                                parent = pid
                                  ? pageIdMap.get(pid)?.q ||
                                    idMap.get(pid)?.q ||
                                    null
                                  : null;
                              }

                              if (!parent) break;

                              if (!ifType) {
                                const t = getIfType(parent.question || "");
                                if (t) ifType = t;
                              }

                              if (looksYesNoQ(parent)) {
                                controllerId = parent.id;
                                break;
                              }

                              cur = parent;
                            }

                            return {
                              isSubQuestion:
                                (!!ifType && !!controllerId) ||
                                (q?.condition === "sub_option" &&
                                  !!controllerId),
                              ifType,
                              controllerId,
                            };
                          };

                          return (
                            <div
                              key={`${mainIdx}-${secIdx}-${subIdx}`}
                              className={
                                [
                                  "Entity Details",
                                  "Reporting Boundary",
                                  "Listings / Registration",
                                ].includes(subSectionName)
                                  ? classes.sideBySide
                                  : classes.fullSide
                              }
                            >
                              {subSectionName &&
                                (() => {
                                  const normalized = (subSectionName || "")
                                    .trim()
                                    .toLowerCase();
                                  const isPrincipleHeading =
                                    normalized.startsWith("principle");
                                  const shouldShow =
                                    !isPrincipleHeading ||
                                    !displayedPrinciples.has(subSectionName);
                                  if (isPrincipleHeading) {
                                    displayedPrinciples.add(subSectionName);
                                  }
                                  return (
                                    shouldShow && (
                                      <Typography
                                        fontSize={19}
                                        fontWeight={600}
                                        paddingLeft={4}
                                        paddingTop={2}
                                        paddingBottom={2}
                                        width="100%"
                                      >
                                        {isPrincipleHeading
                                          ? subSectionName
                                          : `${subIdx + 1}. ${subSectionName}`}
                                      </Typography>
                                    )
                                  );
                                })()}

                              {/* Questions */}
                              {items.map((q, qIdx) => {
                                if (SOC_CHILD_IDS.has(q.id)) return null;
                                const isTopLevel =
                                  !q.question_id || q.question_id === "None";

                                // --- Robust sub-question detection + inheritance ---
                                const { isSubQuestion, ifType, controllerId } =
                                  resolveSubChain(q, qIdx);

                                const controllerIsHere = controllerId
                                  ? items.some((x) => x.id === controllerId)
                                  : false;

                                if (
                                  !isTopLevel &&
                                  isSubQuestion &&
                                  controllerId &&
                                  !controllerIsHere &&
                                  q.condition !== "sub_option"
                                ) {
                                  const arr =
                                    deferredByController.get(controllerId) ||
                                    [];
                                  arr.push({ q, ifType });
                                  deferredByController.set(controllerId, arr);
                                  return null;
                                }

                                if (
                                  !isTopLevel &&
                                  q.condition === "sub_option" &&
                                  controllerIsHere
                                ) {
                                  return null; // ⬅️ prevents it from appearing after the multiselect
                                }
                                const shouldShowSub = !isSubQuestion
                                  ? true
                                  : q.condition === "sub_option"
                                  ? shouldShowByOption(
                                      controllerId,
                                      q.id,
                                      selectedValues
                                    )
                                  : controllerId
                                  ? showByParentChoice(
                                      ifType,
                                      controllerId,
                                      selectedValues
                                    )
                                  : true;

                                const isLinkedChild = !!(
                                  q?.question_id && q?.question_id !== "None"
                                );
                                const isTextConditional =
                                  /^(\s*if\s+(yes|no)|->\s*if\s+(yes|no)|\s*if\s+available|\s*if\s+so)\b/i.test(
                                    (q?.question || "").replace(
                                      /^[\s.\-–—•([\{'"\u2022]+/g,
                                      ""
                                    )
                                  );

                                const mustGateOnParent =
                                  q.condition === "sub_option" ||
                                  isTextConditional ||
                                  (!!controllerId && !isTopLevel);

                                if (
                                  isSubQuestion &&
                                  mustGateOnParent &&
                                  !shouldShowSub
                                )
                                  return null;

                                // keep your existing isSubQuestion logic (from resolveSubChain)

                                // final numbering rule:
                                const displayIndex =
                                  isTopLevel &&
                                  !isTextConditional &&
                                  q?.condition !== "sub_option"
                                    ? displayCounter++
                                    : null;

                                const gridClass =
                                  q.question_type === "text" ||
                                  q.question_type === "Text Field"
                                    ? classes.inputGrid
                                    : classes.gridItem;
                                const {
                                  title: parsedTitle,
                                  legend: abcLegend,
                                } = splitABCLegend(
                                  q.question,
                                  q.table_header || []
                                );
                                return (
                                  <React.Fragment key={q.id}>
                                    <Grid key={q.id} className={gridClass}>
                                      {q?.question !== "" && (
                                        <>
                                          {q.id ===
                                          "68ad3089ea1e907f8e898a41" ? (
                                            <>
                                              {(() => {
                                                const { heading, bullets } =
                                                  formatSustainabilityRisksQuestion(
                                                    q.question
                                                  );
                                                return (
                                                  <>
                                                    <Typography
                                                      fontSize={18}
                                                      fontWeight={500}
                                                      variant="h3"
                                                      paddingLeft={4}
                                                      paddingBottom={1}
                                                    >
                                                      {displayIndex !== null &&
                                                        `${displayIndex}. `}
                                                      {heading}
                                                    </Typography>

                                                    {bullets.length > 0 && (
                                                      <Box
                                                        paddingLeft={6}
                                                        paddingBottom={2}
                                                      >
                                                        <Typography
                                                          fontSize={16}
                                                          fontWeight={400}
                                                        >
                                                          Consider that time
                                                          horizons can:
                                                        </Typography>
                                                        <Box
                                                          marginTop={1}
                                                          paddingLeft={6}
                                                        >
                                                          {bullets.map(
                                                            (b, i) => (
                                                              <Typography
                                                                key={i}
                                                                fontSize={15}
                                                                fontWeight={400}
                                                                style={{
                                                                  marginBottom: 4,
                                                                }}
                                                              >
                                                                {b}
                                                              </Typography>
                                                            )
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    )}
                                                  </>
                                                );
                                              })()}
                                            </>
                                          ) : (
                                            <Typography
                                              fontSize={18}
                                              fontWeight={500}
                                              variant="h3"
                                              paddingLeft={4}
                                              paddingBottom={2}
                                            >
                                              {displayIndex !== null &&
                                                `${displayIndex}. `}
                                              {q.question_type === "table" &&
                                              abcLegend.length
                                                ? parsedTitle
                                                : q?.question
                                                    ?.split("(")
                                                    .map((part, index) =>
                                                      index === 0
                                                        ? part.trim()
                                                        : `(${part}`
                                                    )
                                                    .join(" ")}
                                            </Typography>
                                          )}
                                        </>
                                      )}

                                      <Box
                                        display="flex"
                                        paddingLeft={6}
                                        flexWrap="wrap"
                                      >
                                        {/* SINGLE SELECT */}
                                        {(q.question_type ===
                                          "choose_from_option" ||
                                          q.question_type ===
                                            "Choose from Options") && (
                                          <div
                                            style={{
                                              display: "grid",
                                              gridTemplateColumns:
                                                VERTICAL_ONLY_IDS.has(q.id)
                                                  ? "1fr"
                                                  : `repeat(${
                                                      q.options?.some(
                                                        (o) =>
                                                          o.split(" ").length >
                                                          6
                                                      )
                                                        ? 2
                                                        : 4
                                                    }, 1fr)`,
                                              gap: "16px",
                                              textAlign: "left",
                                            }}
                                          >
                                            {q.options?.map((option, idx) => (
                                              <React.Fragment key={idx}>
                                                <FormControlLabel
                                                  control={
                                                    <Checkbox
                                                      className={
                                                        classes.selectCheckbox
                                                      }
                                                      checked={
                                                        selectedValues?.[
                                                          q.id
                                                        ] === option
                                                      }
                                                      onChange={() =>
                                                        handleSingleSelect(
                                                          q.id,
                                                          option
                                                        )
                                                      }
                                                      value={option}
                                                      sx={{
                                                        color:
                                                          "#5A404E !important",
                                                        "&.Mui-checked": {
                                                          color:
                                                            "#8C2148 !important",
                                                        },
                                                      }}
                                                    />
                                                  }
                                                  label={option}
                                                />
                                                {q.id === CAPACITY_ID &&
                                                  selectedValues?.[q.id] ===
                                                    option && (
                                                    <div
                                                      style={{
                                                        gridColumn: "1 / -1",
                                                        width: 700,
                                                        marginTop: 12,
                                                      }}
                                                    >
                                                      <TextField
                                                        variant="outlined"
                                                        placeholder="Please type your Answer"
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        value={
                                                          textAnswers[
                                                            optionTextKey(
                                                              q.id,
                                                              option
                                                            )
                                                          ] || ""
                                                        }
                                                        onChange={(e) => {
                                                          const v =
                                                            e.target.value;
                                                          handleTextInputChange(
                                                            optionTextKey(
                                                              q.id,
                                                              option
                                                            ),
                                                            v
                                                          );
                                                        }}
                                                        sx={{
                                                          ...TF_NO_BLUE,
                                                          "& .MuiOutlinedInput-root":
                                                            { borderRadius: 2 },
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                {INLINE_SUBOPTION_PARENTS.has(
                                                  q.id
                                                ) &&
                                                  selectedValues?.[q.id] ===
                                                    option &&
                                                  (
                                                    SUBOPTION_RULES[q.id]?.[
                                                      option
                                                    ] || []
                                                  ).map((cid) => {
                                                    const child = getQById(cid);
                                                    if (!child) return null;
                                                    return (
                                                      <div
                                                        key={cid}
                                                        style={{
                                                          marginLeft: 24,
                                                          marginTop: 8,
                                                        }}
                                                      >
                                                        <Typography
                                                          fontSize={16}
                                                          fontWeight={500}
                                                          style={{
                                                            marginBottom: 6,
                                                          }}
                                                        >
                                                          {"\u2022 "}
                                                          {child.question}
                                                        </Typography>
                                                        {renderChildOptions(
                                                          child
                                                        )}
                                                      </div>
                                                    );
                                                  })}

                                                {selectedValues?.[q.id] &&
                                                  /other/i.test(
                                                    selectedValues[q.id]
                                                  ) &&
                                                  /other/i.test(option) && (
                                                    <div
                                                      style={{
                                                        gridColumn: "1 / -1",
                                                        width: 700,
                                                        marginTop: 12,
                                                      }}
                                                    >
                                                      <TextField
                                                        variant="outlined"
                                                        placeholder="Please type your Answer"
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        value={
                                                          textAnswers[
                                                            `other-${q.id}`
                                                          ] || ""
                                                        }
                                                        onChange={(e) =>
                                                          handleTextInputChange(
                                                            `other-${q.id}`,
                                                            e.target.value
                                                          )
                                                        }
                                                        sx={{
                                                          ...TF_NO_BLUE,
                                                          "& .MuiOutlinedInput-root":
                                                            {
                                                              borderRadius: 2,
                                                            }, // less curved edges
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                              </React.Fragment>
                                            ))}
                                          </div>
                                        )}

                                        {/* MULTI SELECT */}
                                        {q.question_type ===
                                          "multiselect_check_box" && (
                                          <div
                                            style={{
                                              display: "grid",
                                              gridTemplateColumns:
                                                VERTICAL_ONLY_IDS.has(q.id)
                                                  ? "1fr"
                                                  : `repeat(${
                                                      q.options?.some(
                                                        (o) =>
                                                          o.split(" ").length >
                                                          6
                                                      )
                                                        ? 2
                                                        : 4
                                                    }, 1fr)`,

                                              gap: "16px",
                                              textAlign: "left",
                                            }}
                                          >
                                            {q.options?.map((option, idx) => (
                                              <React.Fragment key={idx}>
                                                <FormControlLabel
                                                  control={
                                                    <Checkbox
                                                      className={
                                                        classes.selectCheckbox
                                                      }
                                                      checked={
                                                        selectedValues?.[
                                                          q.id
                                                        ]?.includes(option) ||
                                                        false
                                                      }
                                                      onChange={(e) =>
                                                        handleCheckboxChange(
                                                          e,
                                                          q.id
                                                        )
                                                      }
                                                      value={option}
                                                      sx={{
                                                        color:
                                                          "#5A404E !important",
                                                        "&.Mui-checked": {
                                                          color:
                                                            "#8C2148 !important",
                                                        },
                                                        marginRight: "8px",
                                                      }}
                                                    />
                                                  }
                                                  label={option}
                                                  sx={{
                                                    margin: "0 !important",
                                                  }}
                                                />
                                                {isOptionPicked(q.id, option) &&
                                                  SUBOPTION_RULES?.[q.id]?.[
                                                    option
                                                  ]?.length > 0 && (
                                                    <Box
                                                      style={{
                                                        gridColumn: "1 / -1",
                                                        width: "100%",
                                                        marginTop: 8,
                                                      }}
                                                    >
                                                      {getOptionChildren(
                                                        q.id,
                                                        option
                                                      ).map((child) =>
                                                        child.id ===
                                                        INLINE_YN_ID ? (
                                                          renderInlineYesNo(
                                                            child
                                                          ) // <-- only this one goes inline
                                                        ) : (
                                                          <React.Fragment
                                                            key={child.id}
                                                          >
                                                            {renderSOCChild(
                                                              child
                                                            )}
                                                          </React.Fragment>
                                                        )
                                                      )}
                                                    </Box>
                                                  )}
                                                {q.id ===
                                                  WHERE_CONCENTRATED_ID &&
                                                  selectedValues?.[
                                                    q.id
                                                  ]?.includes(option) && (
                                                    <div
                                                      style={{
                                                        gridColumn: "1 / -1",
                                                        width: "100%",
                                                        marginTop: 12,
                                                      }}
                                                    >
                                                      <TextField
                                                        variant="outlined"
                                                        placeholder="Please type your Answer"
                                                        multiline
                                                        rows={3}
                                                        value={
                                                          textAnswers[
                                                            optionTextKey(
                                                              q.id,
                                                              option
                                                            )
                                                          ] || ""
                                                        }
                                                        onChange={(e) =>
                                                          handleTextInputChange(
                                                            optionTextKey(
                                                              q.id,
                                                              option
                                                            ),
                                                            e.target.value
                                                          )
                                                        }
                                                        sx={{
                                                          width: "300%",
                                                          "& .MuiOutlinedInput-root":
                                                            { borderRadius: 2 },
                                                          "& .MuiOutlinedInput-root":
                                                            {
                                                              "& fieldset": {
                                                                borderColor:
                                                                  "black", // default border
                                                              },
                                                              "&:hover fieldset":
                                                                {
                                                                  borderColor:
                                                                    "black", // hover border
                                                                },
                                                              "&.Mui-focused fieldset":
                                                                {
                                                                  borderColor:
                                                                    "black", // focus border (remove blue)
                                                                },
                                                            },
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                {q.id !==
                                                  WHERE_CONCENTRATED_ID &&
                                                  selectedValues?.[
                                                    q.id
                                                  ]?.includes(option) &&
                                                  startsWithOther(option) && (
                                                    <div
                                                      style={{
                                                        gridColumn: "1 / -1",
                                                        width: "100%",
                                                        marginTop: 16,
                                                      }}
                                                    >
                                                      <TextField
                                                        variant="outlined"
                                                        placeholder="Please type your Answer"
                                                        multiline
                                                        rows={3}
                                                        value={
                                                          textAnswers[
                                                            `other-${q.id}`
                                                          ] || ""
                                                        }
                                                        onChange={(e) =>
                                                          handleTextInputChange(
                                                            `other-${q.id}`,
                                                            e.target.value
                                                          )
                                                        }
                                                        sx={{
                                                          ...TF_NO_BLUE,
                                                          width: "100%",
                                                          "& .MuiOutlinedInput-root":
                                                            { borderRadius: 2 },
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                              </React.Fragment>
                                            ))}
                                          </div>
                                        )}

                                        {/* LONG TEXT */}
                                        {(q.question_type === "text" ||
                                          q.question_type === "Text Field") && (
                                          <Box minHeight={80} width={"100%"}>
                                            <CeroInput
                                              className={
                                                classes.textAreaContainer
                                              }
                                              rows={3}
                                              placeholder="Please type your Answer"
                                              id={`answer-${q.id}`}
                                              name={`answer-${q.id}`}
                                              label="Please type your Answer"
                                              multiline
                                              value={textAnswers[q.id] || ""}
                                              onChange={(e) =>
                                                handleTextInputChange(
                                                  q.id,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </Box>
                                        )}

                                        {/* SHORT TEXT / EMAIL / PHONE */}
                                        {q.question_type === "short_text" && (
                                          <Box
                                            width={"50%"}
                                            paddingRight={2}
                                            marginBottom={3}
                                          >
                                            <CeroInput
                                              classes={{
                                                container:
                                                  classes.textAreaContainerShort,
                                                input:
                                                  classes.textAreaContainerInput,
                                              }}
                                              rows={3}
                                              placeholder="Please type your Answer"
                                              id={`answer-${q.id}`}
                                              name={`answer-${q.id}`}
                                              label="Please type your Answer"
                                              multiline
                                              value={textAnswers[q.id] || ""}
                                              onChange={(e) =>
                                                handleTextInputChange(
                                                  q.id,
                                                  e.target.value
                                                )
                                              }
                                              error={
                                                q.question?.toLowerCase() ===
                                                  "email id:" &&
                                                !isValidEmail(textAnswers[q.id])
                                                  ? "Email is not valid"
                                                  : ""
                                              }
                                            />
                                          </Box>
                                        )}

                                        {/* DATE */}
                                        {q.question_type === "date" && (
                                          <CeroDate
                                            label="Date"
                                            value={dateAnswers[q.id] || ""}
                                            onChange={(date) =>
                                              handleDateInputChange(q.id, date)
                                            }
                                            year={2025}
                                          />
                                        )}

                                        {/* TABLES (IFRS) */}
                                        {q.question_type === "table" && (
                                          <Grid width="100%">
                                            {/* One-line description ABOVE the table (only for IFRS4) */}
                                            {q.id === IFRS4_ID && (
                                              <Typography
                                                paddingLeft={6}
                                                paddingBottom={1}
                                                fontSize={16}
                                                fontWeight={500}
                                                sx={{ color: "text.primary" }}
                                              >
                                                Use quantitative and qualitative
                                                information to explain.
                                              </Typography>
                                            )}

                                            {/* keep your existing instructions */}
                                            {q.instructions?.map((inst, i) => (
                                              <Typography
                                                paddingLeft={6}
                                                paddingY={1}
                                                key={i}
                                              >
                                                {i + 1}. {inst}
                                              </Typography>
                                            ))}

                                            {/* the table */}
                                            {renderIfrsTable(q)}

                                            {/* Legend UNDER the table (only for IFRS4) */}
                                            {q.id === IFRS4_ID && (
                                              <Box pl={6} pt={1}>
                                                <Typography
                                                  fontSize={16}
                                                  fontWeight={500}
                                                >
                                                  A – Effects on entity’s
                                                  financial position
                                                </Typography>
                                                <Typography
                                                  fontSize={16}
                                                  fontWeight={500}
                                                >
                                                  B – Effects on entity’s
                                                  financial performance
                                                </Typography>
                                                <Typography
                                                  fontSize={16}
                                                  fontWeight={500}
                                                >
                                                  C – Effects on entity’s cash
                                                  flows
                                                </Typography>
                                              </Box>
                                            )}
                                          </Grid>
                                        )}
                                      </Box>

                                      {/* {q.id === SOC_PARENT_ID &&
                                        Array.isArray(
                                          selectedValues?.[SOC_PARENT_ID]
                                        ) && (
                                          <Box
                                            width="100%"
                                            paddingLeft={6}
                                            paddingTop={2}
                                          >
                                            {Object.entries(SOC_GROUPS).map(
                                              ([optionLabel, childIds]) => {
                                                const picked =
                                                  selectedValues[
                                                    SOC_PARENT_ID
                                                  ] || [];
                                                if (
                                                  !picked.includes(optionLabel)
                                                )
                                                  return null;

                                                return (
                                                  <Box
                                                    key={optionLabel}
                                                    marginTop={2}
                                                    marginBottom={1}
                                                  >
                                                    <Typography
                                                      fontSize={16}
                                                      fontWeight={600}
                                                      paddingBottom={1}
                                                    >
                                                      {optionLabel}
                                                    </Typography>

                                                    {childIds.map((cid) => {
                                                      const child =
                                                        getQById(cid);
                                                      if (!child) return null;
                                                      return renderSOCChild(
                                                        child
                                                      );
                                                    })}
                                                  </Box>
                                                );
                                              }
                                            )}
                                          </Box>
                                        )} */}
                                    </Grid>
                                    {deferredByController
                                      .get(q.id)
                                      ?.map(({ q: child, ifType: childIf }) => {
                                        const childShow =
                                          child?.condition === "sub_option"
                                            ? shouldShowByOption(
                                                q.id,
                                                child.id,
                                                selectedValues
                                              )
                                            : showByParentChoice(
                                                childIf,
                                                q.id,
                                                selectedValues
                                              );

                                        if (!childShow) return null;

                                        const childGridClass =
                                          child.question_type === "text" ||
                                          child.question_type === "Text Field"
                                            ? classes.inputGrid
                                            : classes.gridItem;

                                        return (
                                          <Grid
                                            key={child.id}
                                            className={childGridClass}
                                          >
                                            {child?.question && (
                                              <Typography
                                                fontSize={18}
                                                fontWeight={500}
                                                variant="h3"
                                                paddingLeft={4}
                                                paddingBottom={2}
                                              >
                                                {child.question
                                                  .split("(")
                                                  .map((part, index) =>
                                                    index === 0
                                                      ? part.trim()
                                                      : `(${part}`
                                                  )
                                                  .join(" ")}{" "}
                                              </Typography>
                                            )}

                                            <Box
                                              display="flex"
                                              paddingLeft={6}
                                              flexWrap="wrap"
                                            >
                                              {/* choose_from_option */}
                                              {(child.question_type ===
                                                "choose_from_option" ||
                                                child.question_type ===
                                                  "Choose from Options") && (
                                                <div
                                                  style={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                      VERTICAL_ONLY_IDS.has(
                                                        child.id
                                                      )
                                                        ? "1fr"
                                                        : `repeat(${
                                                            child.options?.some(
                                                              (o) =>
                                                                o.split(" ")
                                                                  .length > 5
                                                            )
                                                              ? 2
                                                              : 4
                                                          }, 1fr)`,
                                                    gap: "16px",
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  {child.options?.map(
                                                    (option, idx) => (
                                                      <React.Fragment key={idx}>
                                                        <FormControlLabel
                                                          control={
                                                            <Checkbox
                                                              className={
                                                                classes.selectCheckbox
                                                              }
                                                              checked={
                                                                selectedValues?.[
                                                                  child.id
                                                                ] === option
                                                              }
                                                              onChange={() =>
                                                                handleSingleSelect(
                                                                  child.id,
                                                                  option
                                                                )
                                                              }
                                                              value={option}
                                                              sx={{
                                                                color:
                                                                  "#5A404E !important",
                                                                "&.Mui-checked":
                                                                  {
                                                                    color:
                                                                      "#8C2148 !important",
                                                                  },
                                                              }}
                                                            />
                                                          }
                                                          label={option}
                                                        />
                                                        {selectedValues?.[
                                                          child.id
                                                        ] &&
                                                          /other/i.test(
                                                            selectedValues[
                                                              child.id
                                                            ]
                                                          ) &&
                                                          /other/i.test(
                                                            option
                                                          ) && (
                                                            <TextField
                                                              variant="outlined"
                                                              placeholder="Please type your Answer"
                                                              multiline
                                                              rows={3}
                                                              value={
                                                                textAnswers[
                                                                  `other-${child.id}`
                                                                ] || ""
                                                              }
                                                              onChange={(e) =>
                                                                handleTextInputChange(
                                                                  `other-${child.id}`,
                                                                  e.target.value
                                                                )
                                                              }
                                                              sx={TF_NO_BLUE}
                                                              className={
                                                                classes.input
                                                              }
                                                            />
                                                          )}
                                                      </React.Fragment>
                                                    )
                                                  )}
                                                </div>
                                              )}

                                              {/* multiselect_check_box */}
                                              {child.question_type ===
                                                "multiselect_check_box" && (
                                                <div
                                                  style={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                      VERTICAL_ONLY_IDS.has(
                                                        child.id
                                                      )
                                                        ? "1fr"
                                                        : `repeat(${
                                                            child.options?.some(
                                                              (o) =>
                                                                o.split(" ")
                                                                  .length > 6
                                                            )
                                                              ? 2
                                                              : 4
                                                          }, 1fr)`,
                                                    gap: "16px",
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  {child.options?.map(
                                                    (option, idx) => (
                                                      <React.Fragment key={idx}>
                                                        <FormControlLabel
                                                          control={
                                                            <Checkbox
                                                              className={
                                                                classes.selectCheckbox
                                                              }
                                                              checked={
                                                                selectedValues?.[
                                                                  child.id
                                                                ]?.includes(
                                                                  option
                                                                ) || false
                                                              }
                                                              onChange={(e) =>
                                                                handleCheckboxChange(
                                                                  e,
                                                                  child.id
                                                                )
                                                              }
                                                              value={option}
                                                              sx={{
                                                                color:
                                                                  "#5A404E !important",
                                                                "&.Mui-checked":
                                                                  {
                                                                    color:
                                                                      "#8C2148 !important",
                                                                  },
                                                                marginRight:
                                                                  "8px",
                                                              }}
                                                            />
                                                          }
                                                          label={option}
                                                          sx={{
                                                            margin:
                                                              "0 !important",
                                                          }}
                                                        />
                                                        {selectedValues?.[
                                                          child.id
                                                        ] &&
                                                          /other/i.test(
                                                            (
                                                              selectedValues[
                                                                child.id
                                                              ] || []
                                                            ).join(",")
                                                          ) &&
                                                          /other/i.test(
                                                            option
                                                          ) && (
                                                            <div
                                                              style={{
                                                                gridColumn:
                                                                  "1 / -1",
                                                                width: "100%",
                                                                marginTop: 16,
                                                              }}
                                                            >
                                                              <TextField
                                                                variant="outlined"
                                                                placeholder="Please type your Answer"
                                                                multiline
                                                                rows={3}
                                                                value={
                                                                  textAnswers[
                                                                    `other-${child.id}`
                                                                  ] || ""
                                                                }
                                                                onChange={(e) =>
                                                                  handleTextInputChange(
                                                                    `other-${child.id}`,
                                                                    e.target
                                                                      .value
                                                                  )
                                                                }
                                                                sx={{
                                                                  width: "100%",
                                                                  TF_NO_BLUE,
                                                                  "& .MuiOutlinedInput-root":
                                                                    {
                                                                      borderRadius: 2,
                                                                    },
                                                                }}
                                                              />
                                                            </div>
                                                          )}
                                                      </React.Fragment>
                                                    )
                                                  )}
                                                </div>
                                              )}

                                              {/* text / short_text / date / table-> child */}
                                              {(child.question_type ===
                                                "text" ||
                                                child.question_type ===
                                                  "Text Field") && (
                                                <Box
                                                  minHeight={80}
                                                  width={"100%"}
                                                >
                                                  <CeroInput
                                                    className={
                                                      classes.textAreaContainer
                                                    }
                                                    rows={3}
                                                    placeholder="Please type your Answer"
                                                    id={`answer-${child.id}`}
                                                    name={`answer-${child.id}`}
                                                    label="Please type your Answer"
                                                    multiline
                                                    value={
                                                      textAnswers[child.id] ||
                                                      ""
                                                    }
                                                    onChange={(e) =>
                                                      handleTextInputChange(
                                                        child.id,
                                                        e.target.value
                                                      )
                                                    }
                                                  />
                                                </Box>
                                              )}

                                              {child.question_type ===
                                                "short_text" && (
                                                <Box
                                                  width={"50%"}
                                                  paddingRight={2}
                                                  marginBottom={3}
                                                >
                                                  <CeroInput
                                                    classes={{
                                                      container:
                                                        classes.textAreaContainerShort,
                                                      input:
                                                        classes.textAreaContainerInput,
                                                    }}
                                                    rows={3}
                                                    placeholder="Please type your Answer"
                                                    id={`answer-${child.id}`}
                                                    name={`answer-${child.id}`}
                                                    label="Please type your Answer"
                                                    multiline
                                                    value={
                                                      textAnswers[child.id] ||
                                                      ""
                                                    }
                                                    onChange={(e) =>
                                                      handleTextInputChange(
                                                        child.id,
                                                        e.target.value
                                                      )
                                                    }
                                                  />
                                                </Box>
                                              )}

                                              {child.question_type ===
                                                "date" && (
                                                <CeroDate
                                                  label="Date"
                                                  value={
                                                    dateAnswers[child.id] || ""
                                                  }
                                                  onChange={(date) =>
                                                    handleDateInputChange(
                                                      child.id,
                                                      date
                                                    )
                                                  }
                                                  year={2025}
                                                />
                                              )}

                                              {child.question_type ===
                                                "table" && (
                                                <Grid width={"100%"}>
                                                  {child.instructions?.map(
                                                    (inst, i) => (
                                                      <Typography
                                                        paddingLeft={6}
                                                        paddingY={1}
                                                        key={i}
                                                      >
                                                        {i + 1}. {inst}
                                                      </Typography>
                                                    )
                                                  )}
                                                  {renderIfrsTable(child)}
                                                </Grid>
                                              )}
                                            </Box>
                                          </Grid>
                                        );
                                      })}{" "}
                                    {/* <-- closes deferred children .map */}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          <Box className={classes.navFooter}>
            {page > 1 && (
              <button
                type="button"
                className={`${classes.navBtn} ${classes.navPrevButton}`}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
            )}

            {page < 3 && (
              <button
                type="button"
                className={`${classes.navBtn} ${classes.navNextButton}`}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            )}
          </Box>
        </Grid>
      )}
    </div>
  );
}

export default IfrsQuestionnaire;
